import type { ScoreConversion, ScoringScale } from "./types";

/**
 * Piecewise-linear interpolation through (x, y) points sorted ascending by x,
 * with linear extrapolation off each end using the nearest segment's slope.
 */
function interpolate(points: { x: number; y: number }[], x: number): number {
  if (points.length === 0) return x;
  if (points.length === 1) return points[0].y;

  if (x <= points[0].x) {
    const [a, b] = [points[0], points[1]];
    return a.y + ((x - a.x) * (b.y - a.y)) / (b.x - a.x);
  }
  const last = points.length - 1;
  if (x >= points[last].x) {
    const [a, b] = [points[last - 1], points[last]];
    return b.y + ((x - b.x) * (b.y - a.y)) / (b.x - a.x);
  }
  for (let i = 0; i < last; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (x >= a.x && x <= b.x) {
      return a.y + ((x - a.x) * (b.y - a.y)) / (b.x - a.x);
    }
  }
  return points[last].y;
}

/**
 * Map a raw paper percentage (0–100) to the board's reported band, clamped to
 * the board's scoring range and rounded to one decimal place.
 */
export function bandFromScorePct(
  conversion: ScoreConversion,
  scale: ScoringScale,
  scorePct: number,
): number {
  const raw = interpolate(
    conversion.anchors.map((a) => ({ x: a.pct, y: a.band })),
    scorePct,
  );
  const clamped = Math.min(scale.max, Math.max(scale.min, raw));
  return Math.round(clamped * 10) / 10;
}

/**
 * Estimate a candidate's national percentile (1–99) for a given band, from the
 * board's national distribution points.
 */
export function nationalPercentileForBand(
  conversion: ScoreConversion,
  band: number,
): number {
  const raw = interpolate(
    conversion.nationalPercentiles.map((p) => ({ x: p.band, y: p.percentile })),
    band,
  );
  return Math.round(Math.min(99, Math.max(1, raw)));
}
